import {useMemo} from 'react';
import {DataTableSortStatus} from 'mantine-datatable';
import {useTableSort} from '@flexinet/ui-components';

import {useProductsInfiniteQuery} from './useProductsQuery';
import {useProductsSearch} from './useProductsSearch';
import {Product} from '../types';

// Newest created first. `created` is AWSDateTime! (never null) and immutable,
// so the default cannot degrade into arrival order and the list does not
// reorder under a reader. `updated` moves on every edit and is never the
// default. The API returns rows in id order, so first paint cannot match API
// order: the sort is applied client-side from the first render.
const DEFAULT_SORT_STATUS: DataTableSortStatus<Product> = {
    columnAccessor: 'created',
    direction: 'desc',
};

const isProduct = (value: unknown): value is Product =>
    typeof value === 'object' && value !== null && typeof (value as Product).id === 'string';

const matchesKeyword = (item: Product, needle: string): boolean =>
    !needle
    || item.name.toLowerCase().includes(needle)
    || item.summary.toLowerCase().includes(needle);

export const useProducts = () => {
    const query = useProductsInfiniteQuery();

    // The keyword comes from the module-scoped store, so what is typed into the
    // toolbar in a SIBLING component reaches this filter (FNP-815).
    const {keyword} = useProductsSearch();
    const needle = keyword.trim().toLowerCase();

    const items = useMemo(
        () => (query.data?.pages ?? []).flatMap(page => page?.items ?? []).filter(isProduct),
        [query.data]
    );

    const matching = useMemo(
        () => items.filter(item => matchesKeyword(item, needle)),
        [items, needle]
    );

    // Client-side sort and search cover ONLY the pages fetched so far. Token
    // pagination cannot order or search rows the browser has not loaded; the
    // table footer says "loaded", never "total", for the same reason.
    // Sort state is component-level (this hook), not module store state: it is
    // how ONE operator is looking at the table right now.
    const {sorted, sortStatus, setSortStatus} = useTableSort(matching, DEFAULT_SORT_STATUS);

    const pages = query.data?.pages ?? [];
    const nextToken = pages[pages.length - 1]?.nextToken ?? null;

    return {
        items,
        visibleItems: sorted,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        // A refetch (Reload) shows on the table; fetching the next page does
        // not, it has its own indicator on the footer.
        fetching: query.isFetching && !query.isFetchingNextPage,
        isError: query.isError,
        nextToken,
        isFetchingMore: query.isFetchingNextPage,
        fetchRecords: () => {
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        reload: () => query.refetch(),
    };
};
